import { createGlobalStyle } from 'styled-components'
import { useEffect } from 'react'
import { QueryClient } from 'react-query'
import { dehydrate } from 'react-query/hydration'
import { useCountryCode } from 'querys/payment'
import Navigation from 'components/navigation'
import { prefetchSubscriptionPlans, useSubscriptionPlans } from 'querys/subscriptions'
import { prefetchSmallCoursesList } from 'querys/course'
import InternalError from 'errors/internal-error'
import Footer from 'common/footer'
import OpenGraph from 'common/open-graph'
import ReasonsToStudy from 'components/prices/reasons-to-study'
import PricingSection from 'components/prices/pricing-section'
import Divider from 'common/divider'

const GlobalStyle = createGlobalStyle`
  body {
    background: black;
  }
  .pricing-section-container h1,
  .reasons-to-study h1,
  .reasons-to-study h2::before {
    color: #FBE835;
    background-image: none;
    background-clip: initial;
    -webkit-background-clip: initial;
    -webkit-text-fill-color: initial;
    text-shadow: initial;
  }
  .reasons-to-study {
    color: white;
    h2 {
      color: var(--neutral10);
    }
    .img-wrapper::after {
      background: linear-gradient(66deg,#000000e6 26.61%,rgba(4,0,30,0) 54.45%);
    }
    .methodology-item::before {
      background: var(--black);
      border: 1px solid #FBE835;
      width: 8px;
      height: 8px;
    }
    .learning-miniature {
      box-shadow: 0px 0px 190px rgb(216 178 19 / 34%)!important;
    }
  }
  .exclusive {
    display: block;
    margin-block-start: 16px;
    font: var(--smallBold);
    width: fit-content;
    padding: 4px 8px;
    background: var(--black);
    border: 1px solid #FBE835;
    color: #FBE835;
    font-weight: 500;
  }
  .pricing-item {
    color: white;
    &.active {
      background: #3b3f5680;
    }
    .plan-checkbox {
      color: #FBE835;
    }
  }
  .pricing-detail {
    color: var(--white);
    background: #3b3f5680;
  }
`

function Prices() {
  const { error } = useSubscriptionPlans()
  const { refetch } = useCountryCode()

  useEffect(() => {
    refetch()
  }, [])

  if (error?.message === 'User Unauthorized') return null
  if (error?.message === 'Internal Error') return <InternalError />

  return (
    <>
      <GlobalStyle />
      <OpenGraph
        title="¡La práctica hace al maestro!"
        description="Esto es aprendizaje basado en proyectos"
        url="https://leonidasesteban.com/precios"
      />
      <Navigation noCountdown />
      <PricingSection />
      <Divider color="transparent" marginVertical={50} />
      <ReasonsToStudy />
      <Footer />
    </>
  )
}

Prices.pageTheme = {
  background: 'var(--sky)',
}

export async function getStaticPaths() {
  const countries = ['mx', 'international']
  const paths = []

  countries.forEach((country) => {
    paths.push({ params: { variant: `${country}` } })
  })

  return {
    paths,
    fallback: false,
  }
}

export async function getStaticProps(context) {
  const { params: { variant } } = context

  const queryClient = new QueryClient()
  const countryCode = variant === 'international' ? 'US' : 'MX'

  queryClient.setQueryData(
    ['GET_COUNTRY_CODE', null],
    () => ({
      countryCode,
    }),
  )
  await prefetchSubscriptionPlans({ queryClient, countryCode })
  await prefetchSubscriptionPlans({ queryClient, coupon_code: 'ppp', countryCode })
  await prefetchSmallCoursesList({ queryClient, countryCode })

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 600,
  }
}

export default Prices
